import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Platform,
  TouchableOpacity, 
  ScrollView,
  Image,
  Modal,
  Alert,
  ActivityIndicator,
  useWindowDimensions
} from 'react-native';
import { useProfileStore } from '../../contexts/StoreContext';
import { formatAgeDisplay, calculateAgeInMonths, ChildProfile, DomainKey } from '../../types/profile';
import { NavigationProps } from '../../types/navigation';
import { appConfig, ConfigManager } from '../../config/configManager';
import { Base64ImageStorage, extractImgSrc } from '../../storage/Base64ImageStorage';
import { AddProfileView } from './AddProfileView';
import { DebugView } from './DebugView';
import { checkDomainStaleness, loadStalenessThreshold, getStalenessLabel, loadShowLastUpdateSetting, loadConcernFreshnessThreshold, formatComputationTimestamp } from '../../utils/milestoneStaleness';
import { StoredConcernData, CONCERN_STORAGE_KEY, getConcernDomains } from '../../types/concerns';
import { ActionDefStorage, ActionDefDto } from '../../storage/ActionDefStorage';
import { ActionPlanSummary } from './ActionPlanSummary';

// Helper function to get developmental data for table display
const getDevelopmentalSummaryData = (profile: ChildProfile | null, stalenessThreshold: string = '2weeks') => {
  if (!profile) return [];

  // Ensure all required properties exist with defaults
  if (!profile.devage || !profile.devdelay || !profile.devresultscore) {
    console.warn('Profile missing required developmental data properties');
    return [];
  }

  //const isPremium = profile?.isPremium ?? appConfig.isPremium(); // ✓ Use profile first
  const isPremium = appConfig.isPremium(); // ✓ Use account-level status

  const domainNames = {
    communication: 'Communication',
    motor: 'Motor Skills',
    finemotor: 'Fine Motor',
    grossmotor: 'Gross Motor', 
    cognitive: 'Cognitive',
    socialemotional: 'Social-Emotional',
  };

  // Get domains based on premium status
  const domains: DomainKey[] = isPremium 
    ? ['communication', 'grossmotor', 'finemotor', 'cognitive', 'socialemotional']
    : ['communication', 'motor', 'cognitive', 'socialemotional'];



  return domains.map(domain => {
    const computationTimestamp = profile.devresultcomputed?.[domain] || null;
    const staleness = checkDomainStaleness(computationTimestamp, stalenessThreshold);
    const capCode = profile.devresult?.[domain] ?? null;
    const isCapped = [4, 5].includes(capCode ?? -1);

    let capDirection: 'floor' | 'ceiling' | null = null;
    if (isCapped) {
      const devAge = profile.devage?.[domain] ?? null;
      const floorAge = profile.devfloor?.[domain] ?? null;
      const ceilingAge = profile.devceiling?.[domain] ?? null;
      const physicalAge = profile.agephysical ?? null;
      const tol = 0.11;

      const nearFloor =
        typeof devAge === 'number' &&
        typeof floorAge === 'number' &&
        Math.abs(devAge - floorAge) <= tol;
      const nearCeiling =
        typeof devAge === 'number' &&
        typeof ceilingAge === 'number' &&
        Math.abs(devAge - ceilingAge) <= tol;

      if (nearFloor && !nearCeiling) {
        capDirection = 'floor';
      } else if (nearCeiling && !nearFloor) {
        capDirection = 'ceiling';
      } else {
        // Legacy profiles may have a generic cap code. Use score position vs physical age
        // as a deterministic fallback when floor/ceiling proximity is inconclusive.
        if (typeof devAge === 'number' && typeof physicalAge === 'number') {
          capDirection = devAge <= physicalAge ? 'floor' : 'ceiling';
        } else {
          capDirection = capCode === 5 ? 'floor' : 'ceiling';
        }
      }

      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('HomeView capDirection', {
          domain,
          capCode,
          devAge,
          floorAge,
          ceilingAge,
          physicalAge,
          nearFloor,
          nearCeiling,
          resolved: capDirection
        });
      }
    }
    
    return {
      domain: domainNames[domain],
      key: domain,
      devage: profile.devage?.[domain] ?? null,
      devdelay: profile.devdelay?.[domain] ?? null, 
      devresult: profile.devresultscore?.[domain] ?? null,
      isCapped,
      capDirection,
      staleness,
      stalenessLabel: getStalenessLabel(staleness)
    };
  });
};

// Domain mapping function to convert HomeView domain keys to MilestonesView domain keys
const mapDomainForMilestones = (homeViewDomainKey: string, isPremium: boolean): string => {
  const domainMap: { [key: string]: string } = {
    communication: 'communication',
    motor: 'motor',
    grossmotor: 'grossMotor', 
    finemotor: 'fineMotor',
    cognitive: 'cognitive',
    socialemotional: 'socialEmotional',
  };
  
  return domainMap[homeViewDomainKey] || homeViewDomainKey;
};

// ── Home summary helpers ───────────────────────────────────────────────────────────────────────
function getSkillResultLabel(score: number | null | undefined, isCapped: boolean = false): { label: string; color: string } {
  if (isCapped) {
    return { label: 'Max', color: '#0f766e' };
  }
  if (score === null || score === undefined || typeof score !== 'number' || isNaN(score)) {
    return { label: 'Not assessed', color: '#94a3b8' };
  }
  if (score >= 100) return { label: 'Advanced',  color: '#16a34a' };
  if (score >= 50)  return { label: 'Good',       color: '#0d9488' };
  if (score > 0)    return { label: 'Needs Work', color: '#d97706' };
  return               { label: 'Needs Help', color: '#dc2626' };
}

interface ProfileDropdownProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectProfile: (profileId: string) => void;
  onAddProfile: () => void;
  profiles: any[];
  activeProfileId: string | null;
  canAddProfile: boolean;
  maxProfiles: number;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  isVisible,
  onClose,
  onSelectProfile,
  onAddProfile,
  profiles,
  activeProfileId,
  canAddProfile,
  maxProfiles,
}) => {
  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity style={styles.modalOverlay} onPress={onClose} activeOpacity={1}>
        <TouchableOpacity style={styles.dropdownContainer} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.dropdownTitle}>Select Profile</Text>
          
          {profiles.map((profile) => (
            <TouchableOpacity
              key={profile.id}
              style={[
                styles.profileOption,
                profile.id === activeProfileId && styles.activeProfileOption
              ]}
              onPress={() => {
                onSelectProfile(profile.id);
                onClose();
              }}
            >
              <View style={styles.profileOptionContent}>
                <View style={styles.dropdownAvatar}>
                  {profile.photo ? (
                    <Image source={{ uri: profile.photo }} style={styles.dropdownAvatarImage} />
                  ) : (
                    <Text style={styles.dropdownAvatarEmoji}>
                      {profile.gender === 'male' ? '\uD83D\uDC66' : profile.gender === 'female' ? '\uD83D\uDC67' : '\uD83E\uDDD2'}
                    </Text>
                  )}
                </View>
                <View style={styles.profileInfo}>
                  <View style={styles.dropdownNameRow}>
                    <Text style={styles.dropdownProfileName}>{profile.name || 'Unnamed Profile'}</Text>
                    {profile.isDirty && (
                      <View style={styles.dropdownDirtyDot} />
                    )}
                  </View>
                  <Text style={styles.dropdownProfileAge}>
                    {(calculateAgeInMonths(profile.dob)).toFixed(1)} months old
                  </Text>
                  {profile.isPremium && (
                    <Text style={styles.premiumBadge}>Premium</Text>
                  )}
                </View>
              </View>
            </TouchableOpacity>
          ))}
          
          <TouchableOpacity 
            style={[styles.addProfileButton, !canAddProfile && styles.addProfileButtonDisabled]}
            disabled={!canAddProfile}
            onPress={() => {
              console.log('🔵 ProfileDropdown: Add Profile button pressed');
              onAddProfile();
            }}
          >
            <Text style={[styles.addProfileText, !canAddProfile && styles.addProfileTextDisabled]}>
              {canAddProfile ? '+ Add New Profile' : `Profile Limit Reached (${maxProfiles})`}
            </Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

// Helper function to get the oldest computation timestamp across all domains
const getOldestComputationTimestamp = (profile: ChildProfile | null): { timestamp: string | null; domain: string | null } => {
  if (!profile || !profile.devresultcomputed) {
    return { timestamp: null, domain: null };
  }

  const isPremium = appConfig.isPremium();
  //const isPremium = profile?.isPremium ?? appConfig.isPremium(); // ✓ Use profile first
  const domains: DomainKey[] = isPremium 
    ? ['communication', 'grossmotor', 'finemotor', 'cognitive', 'socialemotional']
    : ['communication', 'motor', 'cognitive', 'socialemotional'];

  let oldestTimestamp: string | null = null;
  let oldestDomain: string | null = null;

  for (const domain of domains) {
    const timestamp = profile.devresultcomputed[domain];
    if (timestamp) {
      if (!oldestTimestamp || new Date(timestamp) < new Date(oldestTimestamp)) {
        oldestTimestamp = timestamp;
        oldestDomain = domain;
      }
    }
  }

  return { timestamp: oldestTimestamp, domain: oldestDomain };
};

interface DevelopmentalSummaryTableProps {
  profile: ChildProfile;
  onTabChange: (tab: NavigationProps['activeTab']) => void;
  onNavigateWithDomain?: (tab: NavigationProps['activeTab'], domain?: string) => void;
  stalenessThreshold: string;
  showLastUpdate?: boolean;
}

const DevelopmentalSummaryTable: React.FC<DevelopmentalSummaryTableProps> = ({ profile, onTabChange, onNavigateWithDomain, stalenessThreshold, showLastUpdate = false }) => {
  const summaryData = getDevelopmentalSummaryData(profile, stalenessThreshold);

  // Safety check - if no data available, show a message
  if (!summaryData || summaryData.length === 0) {
    return (
      <View style={styles.loadingState}>
        <Text style={styles.loadingText}>Developmental data not available</Text>
      </View>
    );
  }

  const formatValue = (value: number | null, type: 'age' | 'score' | 'delay'): string => {
    // Handle null/undefined values
    if (value === null || value === undefined) return '?';
    
    // Handle non-numeric values
    if (typeof value !== 'number' || isNaN(value)) return '?';
    
    // For scores, show as integer
    if (type === 'score') {
      return Math.round(value).toString();
    }
    
    // For ages and delays, 0 means ?
    if (value === 0 && (type === 'age' || type === 'delay')) return '?';
    
    if (type === 'age') {
      return `${Math.abs(value).toFixed(1)}m`;
    }
    
    if (type === 'delay') {
      return value > 0 ? `+${value.toFixed(1)}m` : `${value.toFixed(1)}m`;
    }
    
    return value.toFixed(1);
  };

  const getDelayColor = (delay: number | null): string => {
    if (delay === null || delay === undefined || delay === 0) return '#95a5a6';
    if (typeof delay !== 'number' || isNaN(delay)) return '#95a5a6';
    if (delay > 2) return '#e74c3c'; // Significant delay
    if (delay > 0) return '#f39c12'; // Minor delay  
    return '#27ae60'; // Advanced
  };

  const getResultBackgroundColor = (result: number | null, isCapped: boolean): string => {
    if (isCapped) {
      return '#d1d5db';
    }
    if (result === null || result === undefined || typeof result !== 'number' || isNaN(result)) {
      return 'transparent';
    }
    
    if (result <= 0) return '#ff7a7aff'; // Darker red
    if (result > 0 && result < 50) return '#edff48ff'; // Darker yellow
    if (result >= 50 && result < 100) return '#b7e0c1ff'; // Light green
    return '#5cb85c'; // Medium green
  };

  return (
    <View style={styles.summaryTable}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        <Text style={[styles.tableHeaderText, styles.domainColumn]}>Domain</Text>
        <Text style={[styles.tableHeaderText, styles.ageColumn]}>Age</Text>
        <Text style={[styles.tableHeaderText, styles.delayColumn]}>Delay</Text>
        <Text style={[styles.tableHeaderText, styles.scoreColumn]}>Result</Text>
      </View>
      
      {/* Table Rows */}
      {summaryData.map((item, index) => (
        <TouchableOpacity 
          key={item.key} 
          style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}
          onPress={() => {
            const isPremium = appConfig.isPremium();
            //const isPremium = profile?.isPremium ?? appConfig.isPremium(); // ✓ Use profile first
            const mappedDomain = mapDomainForMilestones(item.key, isPremium);
            console.log('Navigating to Milestones with domain:', item.key, '-> mapped:', mappedDomain);
            if (onNavigateWithDomain) {
              onNavigateWithDomain('Milestones', mappedDomain);
            } else {
              onTabChange('Milestones');
            }
          }}
        >
          <View style={[styles.domainColumn, { flexDirection: 'row', alignItems: 'center' }]}>
            <Text style={[
              styles.tableText,
              {
                backgroundColor: appConfig.getNavButtonColor(),
                color: '#ffffff',
                borderRadius: 6,
                fontWeight: 'bold',
                paddingVertical: 6,
                paddingHorizontal: 6,
                flex: 1
              }
            ]} numberOfLines={2}>{item.domain}</Text>
          </View>
          <Text style={[styles.tableText, styles.ageColumn]}>
            {item.isCapped ? `${formatValue(item.devage, 'age')} ${item.capDirection === 'floor' ? '-' : '+'}` : formatValue(item.devage, 'age')}
          </Text>
          <Text style={[
            styles.tableText, 
            styles.delayColumn
          ]}>
            {item.isCapped ? '—' : formatValue(item.devdelay, 'delay')}
          </Text>
          <Text style={[
            styles.tableText, 
            styles.scoreColumn,
            { 
              backgroundColor: getResultBackgroundColor(item.devresult, item.isCapped),
              borderRadius: 6,
              marginLeft: 10,
              paddingVertical: 2,
              paddingHorizontal: 4
            }
          ]}>
            {item.isCapped ? 'Max' : formatValue(item.devresult, 'score')}
          </Text>
        </TouchableOpacity>
      ))}
{/* 
      {summaryData.some(item => item.isCapped) && (
        <View style={styles.stalenessWarning}>
          <Text style={styles.lastUpdateText}>Capped means the window reached the edge of milestones; actual developmental age and result are unknown.</Text>
        </View>
      )} */}
      
      {/* Navigation link */}
      <TouchableOpacity 
        style={styles.navigationLink}
        onPress={() => {
          const isPremium = appConfig.isPremium();
          //const isPremium = profile?.isPremium ?? appConfig.isPremium(); // ✓ Use profile first
          const mappedDomain = mapDomainForMilestones('communication', isPremium);
          console.log('Navigating to Milestones with domain: communication -> mapped:', mappedDomain);
          if (onNavigateWithDomain) {
            onNavigateWithDomain('Milestones', mappedDomain);
          } else {
            onTabChange('Milestones');
          }
        }}
      >
        <Text style={styles.navigationLinkText}>Select a domain to update milestones</Text>
      </TouchableOpacity>

      {/* Conditional staleness warning */}
      {summaryData.some(item => item.staleness.isStale || item.staleness.daysSinceComputed === null) && (
        <View style={styles.stalenessWarning}>
          <Text style={styles.stalenessWarningText}>⚠️ Milestones need updating</Text>
        </View>
      )}

      {/* Show oldest update when debug setting is enabled and no domains are stale */}
      {showLastUpdate && !summaryData.some(item => item.staleness.isStale || item.staleness.daysSinceComputed === null) && (() => {
        const { timestamp } = getOldestComputationTimestamp(profile);
        if (timestamp) {
          const formattedTimestamp = formatComputationTimestamp(timestamp);
          return (
            <View style={styles.stalenessWarning}>
              <Text style={styles.lastUpdateText}>Oldest update: {formattedTimestamp}</Text>
            </View>
          );
        }
        return null;
      })()}
    </View>
  );
};
 
interface DevelopmentDomainCardProps {
  domain: {
    name: string;
    ageMonths: number;
    percentile?: number;
  };
  chronologicalAge: number;
  isPremium: boolean;
}

const DevelopmentDomainCard: React.FC<DevelopmentDomainCardProps> = ({ 
  domain, 
  chronologicalAge, 
  isPremium 
}) => {
  const ageString = formatAgeDisplay(domain.ageMonths);
  const isAdvanced = domain.ageMonths > chronologicalAge;
  const isDelayed = domain.ageMonths < chronologicalAge - 2; // More than 2 months behind
  
  const getStatusColor = () => {
    if (isDelayed) return '#e74c3c';
    if (isAdvanced) return '#27ae60';
    return '#3498db';
  };

  return (
    <View style={[styles.domainCard, { borderLeftColor: getStatusColor() }]}>
      <View style={styles.domainHeader}>
        <Text style={styles.domainName}>{domain.name}</Text>
        {isPremium && domain.percentile && (
          <Text style={styles.percentile}>{domain.percentile}%</Text>
        )}
      </View>
      <Text style={[styles.domainAge, { color: getStatusColor() }]}>
        {ageString}
      </Text>
      {domain.percentile && (
        <Text style={styles.domainSubtext}>
          {isAdvanced ? 'Advanced' : isDelayed ? 'Needs attention' : 'On track'}
        </Text>
      )}
    </View>
  );
};

export const HomeView: React.FC<NavigationProps & { 
  onNavigateWithDomain?: (tab: NavigationProps['activeTab'], domain?: string) => void;
  storage?: any;
  apiService?: any;
  contentService?: any;
  useContentStore?: any;
  bundleStatus?: { isLoaded: boolean };
}> = ({ onTabChange, onNavigateWithDomain, storage, apiService, contentService, useContentStore, bundleStatus }) => {
  console.log('🏠 HomeView: Component rendering');
  
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showDebugView, setShowDebugView] = useState(false);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [stalenessThreshold, setStalenessThreshold] = useState('2weeks');
  const [concernFreshnessThreshold, setConcernFreshnessThreshold] = useState('2weeks');
  const [showLastUpdate, setShowLastUpdate] = useState(true); // Default enabled to match DebugView
  const [isSyncing, setIsSyncing] = useState(false);
  const autoSyncAttemptedProfileIdRef = useRef<string | null>(null);
  const [logoImgSrc, setLogoImgSrc] = useState<string | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [concernData, setConcernData] = useState<StoredConcernData | null>(null);
  const [actionDef, setActionDef] = useState<ActionDefDto | null>(null);

  //CB:20260627
  const [configVersion, setConfigVersion] = useState(0);

  // Load ActionDef catalogue for the Action Plan summary card.
  // Also re-fires when bundleStatus.isLoaded flips to true so that a cold-start
  // (cache-wiped) load re-reads after bundled-content.json has been unpacked.
  useEffect(() => {
    const activeStorage = storage || (globalThis as any).__childNavStorage;
    if (!activeStorage) return;
    new ActionDefStorage(activeStorage).getActionDef().then(def => setActionDef(def)).catch(() => {});
  }, [storage, bundleStatus?.isLoaded]);

  // Subscribe to Base64ImageStorage save notifications so the logo reloads after download
  useEffect(() => {
    const unsub = ConfigManager.addLogoImageListener(() => setLogoVersion(v => v + 1));
    return unsub;
  }, []);
  
 //CB:20260627
 // Watch for AccountGetInfo to load and update config if premium status changes
  useEffect(() => {
    if (!storage) return;
    
    const checkInterval = setInterval(async () => {
      try {
        const userState = (globalThis as any).__userStore?.getState?.();
        const accountId = userState?.session?.user?.familyId || 
                          userState?.session?.familyAccount?.familyId || '';
        if (accountId) {
          const { AccountUserStorage } = await import('../../storage/AccountUserStorage');
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          
          if (dto && (dto.IsPremium === '1' || dto.IsPremium === '0')) {
            const isPremium = dto.IsPremium === '1';
            const currentTier = appConfig.getConfig().tier;
            const expectedTier = isPremium ? 'premium' : 'standard';
            
            if (currentTier !== expectedTier) {
              console.log('🏠 HomeView: Premium status loaded, updating config', { 
                from: currentTier, 
                to: expectedTier,
                willIncrementConfigVersion: true 
              });
              appConfig.updateConfig({ tier: expectedTier });
              console.log('🏠 After updateConfig, isPremium():', appConfig.isPremium());
              setConfigVersion(v => v + 1); // Force re-render
              clearInterval(checkInterval); // Stop checking
            } else {
              clearInterval(checkInterval); // Already correct, stop checking
            }
          }
        }
      } catch (e) {
        // Ignore errors during check
      }
    }, 500); // Check every 500ms

    // Stop after 10 seconds max
    const timeout = setTimeout(() => clearInterval(checkInterval), 10000);
    
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [storage]);

  // Track if AddProfile was manually triggered by user (not auto-triggered)
  const manualAddProfileRef = useRef(false);
  
  const profileStore = useProfileStore();
  const {
    profiles,
    activeProfileId,
    setActiveProfile,
    addProfile,
    getActiveDevelopmentAge,
    retryDirtyProfiles,
  } = profileStore();

  // Derive activeProfile directly from the reactive `profiles` slice so that any
  // updateProfile call (e.g. from ConcernsView) immediately re-renders HomeView.
  const activeProfile = (profiles as any[]).find(p => p.id === activeProfileId) ?? null;
  const selectableProfiles = (profiles as any[]).filter((p: any) => !p.isHidden && p.profileStatus !== 0);
  const developmentAge = getActiveDevelopmentAge();
  const isPremiumAccount = appConfig.isPremium();
  const maxProfiles = isPremiumAccount ? 10 : 2;
  const canAddProfile = profiles.length < maxProfiles;

  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;

  // Memoize summary data to recalculate when premium status changes
//CB:20260627 - added configVersion to dependency array to force recalculation when premium status changes
// Memoize summary data to recalculate when premium status changes
const summaryData = useMemo(
  () => {
    const isPremiumNow = appConfig.isPremium();
    console.log('🔄 useMemo recalculating summaryData:', {
      configVersion,
      isPremiumNow,
      isPremiumAccount,
      activeProfileId: activeProfile?.id,
      stalenessThreshold
    });
    return activeProfile ? getDevelopmentalSummaryData(activeProfile, stalenessThreshold) : [];
  },
  [activeProfile, stalenessThreshold, configVersion, isPremiumAccount]
);


  // Check if we should show the add profile view when no profiles exist
  useEffect(() => {
    if (profiles.length === 0 && !showAddProfile) {
      setShowAddProfile(true);
      manualAddProfileRef.current = false; // This is auto-triggered, not manual
    } else if (profiles.length > 0 && showAddProfile && !manualAddProfileRef.current) {
      setShowAddProfile(false);
    }
    // Note: Don't hide AddProfileView if it was manually triggered (manualAddProfileRef.current === true)
  }, [profiles.length, showAddProfile]);

  // If current active profile becomes disabled/hidden, switch to the first selectable profile.
  useEffect(() => {
    const currentActive = (profiles as any[]).find((p: any) => p.id === activeProfileId);
    if (!currentActive) return;
    if (currentActive.isHidden || currentActive.profileStatus === 0) {
      const fallback = selectableProfiles[0];
      if (fallback && fallback.id !== activeProfileId) {
        setActiveProfile(fallback.id);
      }
    }
  }, [profiles, activeProfileId, selectableProfiles, setActiveProfile]);

  // Load staleness threshold from storage
  useEffect(() => {
    const loadThreshold = async () => {
      const threshold = await loadStalenessThreshold(storage);
      setStalenessThreshold(threshold);
      const concernThreshold = await loadConcernFreshnessThreshold(storage);
      setConcernFreshnessThreshold(concernThreshold);
      const showLastUpdateSetting = await loadShowLastUpdateSetting(storage);
      setShowLastUpdate(showLastUpdateSetting);
    };
    
    loadThreshold();
  }, [storage]);

  // Load brand logo from Base64ImageStorage using the configured logoImageId
  useEffect(() => {
    const loadLogo = async () => {
      // Try the passed-in storage prop first, fall back to global storage
      const activeStorage = storage || (globalThis as any).__childNavStorage;
      if (!activeStorage) {
        console.warn('🖼️ HomeView loadLogo: no storage available');
        return;
      }
      try {
        const logoImageId = appConfig.getLogoImageId();
        const imageStorage = new Base64ImageStorage(activeStorage);

        // Log index so we can see what's actually stored
        const allImages = await imageStorage.getAllImages();

        let dto = logoImageId ? await imageStorage.getImage(logoImageId) : null;

        // Fallback: use the first available image if the configured ID isn't stored yet
        if (!dto && allImages.length > 0) {
          dto = allImages[0];
        }

        if (dto?.base64) {
          const src = extractImgSrc(dto.base64);
          if (src) {
            setLogoImgSrc(src);
          } 
        } 
      } catch (err) {
        console.warn('🖼️ HomeView loadLogo: error =', err);
      }
    };
    loadLogo();
  }, [storage, logoVersion]);

  // Load concern data from storage — shared helper used by both initial load and invalidation polling
  const loadConcernDataFromStorage = useCallback(async () => {
    const activeStorage = storage || (globalThis as any).__childNavStorage;
    if (!activeStorage) return;
    try {
      const raw = await activeStorage.getItem(CONCERN_STORAGE_KEY);
      if (raw) {
        const parsed: StoredConcernData = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setConcernData(parsed);
      }
    } catch (err) {
      console.warn('HomeView: failed to load concern data', err);
    }
  }, [storage]);

  // Initial load
  useEffect(() => {
    loadConcernDataFromStorage();
  }, [loadConcernDataFromStorage]);

  // Re-load whenever useMilestoneSync (or any other process) extracts fresh concern definitions
  useEffect(() => {
    const lastSeenRef = { value: (globalThis as any).__concernStorageInvalidated ?? 0 };
    const check = () => {
      const current = (globalThis as any).__concernStorageInvalidated ?? 0;
      if (current > lastSeenRef.value) {
        lastSeenRef.value = current;
        loadConcernDataFromStorage();
      }
    };
    check(); // run immediately in case signal already fired before this effect ran
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [loadConcernDataFromStorage]);

  const handleAddProfile = () => {
    if (!canAddProfile) {
      Alert.alert(
        'Profile Limit Reached',
        isPremiumAccount
          ? `Premium accounts can have up to ${maxProfiles} profiles.`
          : `Standard accounts can have up to ${maxProfiles} profiles. Upgrade to Premium for up to 10 profiles.`
      );
      return;
    }
    setDropdownVisible(false);
    manualAddProfileRef.current = true; // Mark as manually triggered
    setShowAddProfile(true);
  };

  const handleProfileCreated = (newProfile: ChildProfile) => {
    addProfile(newProfile);
    setShowAddProfile(false);
    manualAddProfileRef.current = false; // Reset manual flag
    onTabChange('Milestones');
  };

  const handleCancelAddProfile = () => {
    setShowAddProfile(false);
    manualAddProfileRef.current = false; // Reset manual flag when cancelled
    // If no profiles exist and user cancels, show them the option again
    if (profiles.length === 0) {
      Alert.alert(
        'No Profiles',
        'You need at least one profile to use ChildNav. Would you like to create one now?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Create Profile', onPress: () => setShowAddProfile(true) },
        ]
      );
    }
  };

  // Handle direct navigation to debug view
  const handleDebugTools = () => {
    setShowDebugView(true);
  };

  const handleBackFromDebug = () => {
    setShowDebugView(false);
  };

  const handleSyncProfile = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await retryDirtyProfiles();
      console.log('✅ HomeView: Manual sync completed');
    } catch (error) {
      console.error('❌ HomeView: Manual sync failed:', error);
      Alert.alert('Sync Failed', 'Could not sync profile to server. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto-sync once when HomeView is entered with a dirty active profile.
  // If it fails, keep the manual sync button available for retry.
  useEffect(() => {
    if (!activeProfile?.id || !activeProfile.isDirty) return;
    if (isSyncing) return;
    if (autoSyncAttemptedProfileIdRef.current === activeProfile.id) return;

    autoSyncAttemptedProfileIdRef.current = activeProfile.id;

    const runAutoSync = async () => {
      setIsSyncing(true);
      try {
        await retryDirtyProfiles();
        console.log('✅ HomeView: Auto-sync completed');
      } catch (error) {
        // Silent failure for auto-sync; user can manually retry from the sync button.
        console.error('❌ HomeView: Auto-sync failed:', error);
      } finally {
        setIsSyncing(false);
      }
    };

    runAutoSync();
  }, [activeProfile?.id, activeProfile?.isDirty, isSyncing, retryDirtyProfiles]);

  const chronologicalAge = activeProfile ? calculateAgeInMonths(activeProfile.dob) : 0;
  


  // Show add profile view if requested or no profiles exist
  if (showAddProfile) {
    return (
      <AddProfileView
        onProfileCreated={handleProfileCreated}
        onCancel={handleCancelAddProfile}
      />
    );
  }

  // If showing debug view, render that view instead
  if (showDebugView) {
    return (
      <DebugView 
        onBack={handleBackFromDebug}
        storage={storage}
        apiService={apiService}
        contentService={contentService}
        useContentStore={useContentStore}
      />
    );
  }

  // Show empty state if no profiles and not showing add profile view
  if (!activeProfile && profiles.length === 0) {
    return (
      <View style={styles.backgroundContainer}>
        <ScrollView style={styles.container} contentContainerStyle={styles.fullScreenContent}>
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>Welcome to ChildNav!</Text>
            <Text style={styles.emptyDescription}>
              Create your first child profile to get started tracking development milestones.
            </Text>
            <TouchableOpacity style={styles.createProfileButton} onPress={handleAddProfile}>
              <Text style={styles.createProfileButtonText}>Create Profile</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  // Guard clause - ensure we have an active profile
  if (!activeProfile) {
    return (
      <View style={styles.backgroundContainer}>
        <ScrollView style={styles.container} contentContainerStyle={styles.fullScreenContent}>
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>No Active Profile</Text>
            <Text style={styles.emptyDescription}>
              Please select a profile from the dropdown.
            </Text>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={{ maxWidth: appConfig.getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: appConfig.getCardMargin() }}>
        {/* Row 1: Profile Header + Call to Action */}
      <View style={[styles.profileHeader, { marginBottom: 6, backgroundColor: appConfig.getAppBackgroundColor(), flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12 }]}>
        {/* Profile selector: avatar + details + arrow */}
        <TouchableOpacity
          style={styles.profileHeaderTouchable}
          onPress={() => setDropdownVisible(true)}
        >
          <View style={styles.profileAvatarDetailsGroup}>
              <View style={styles.profileAvatar}>
                {activeProfile.photo ? (
                  <Image source={{ uri: activeProfile.photo }} style={styles.avatarImage} />
                ) : (
                  <Text style={styles.avatarText}>
                    {activeProfile.gender === 'male' ? '👦' : activeProfile.gender === 'female' ? '👧' : '🧒'}
                  </Text>
                )}
              </View>
              <View style={styles.profileDetails}>
                <View style={styles.dropdownNameRow}>
                  <Text style={styles.profileName} numberOfLines={1}>{activeProfile.name || 'Unnamed Profile'}</Text>
                  {activeProfile.isDirty && <View style={styles.dropdownDirtyDot} />}
                </View>
                <Text style={styles.profileAge}>
                  {chronologicalAge.toFixed(1)} months old
                </Text>
                {activeProfile.isPremium && (
                  <Text style={styles.premiumBadge}>Premium Account</Text>
                )}
              </View>
            </View>
          <Text style={styles.dropdownArrow}>▼</Text>
        </TouchableOpacity>

        {/* Sync button — right of the dropdown arrow */}
        {activeProfile.isDirty && (
          <TouchableOpacity
            style={[styles.syncIconButton, isSyncing && styles.syncIconButtonDisabled]}
            onPress={handleSyncProfile}
            disabled={isSyncing}
          >
            {isSyncing
              ? <ActivityIndicator size="small" color="#ffffff" />
              : <Text style={styles.syncIconText}>{'🔄'}</Text>}
          </TouchableOpacity>
        )}

        {/* Spacer pushes brand logo to the right */}
        <View style={{ flex: 1 }} />

        {/* Brand logo — right-justified, hidden below 600px */}
        {logoImgSrc && screenWidth >= 600 ? (
          <Image source={{ uri: logoImgSrc }} style={styles.brandLogo} resizeMode="contain" />
        ) : null}
      </View>

        {/* Row 0: How are we doing + Today’s Actions */}
        <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, marginBottom: 20, alignItems: 'stretch' }}>

          {/* How are we doing */}
          <View style={[styles.developmentSection, { flex: isWide ? 1 : undefined, marginBottom: 0 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>How are we doing  🚀</Text>
              <TouchableOpacity onPress={() => onTabChange('Milestones')}>
                <Text style={styles.navigationLinkText}>Update →</Text>
              </TouchableOpacity>
            </View>

            {/* Skills Development */}
            <Text style={styles.homeSubSectionTitle}>Skills Development</Text>
            {(() => {
              // summaryData now comes from useMemo at component level
              //const summaryData = getDevelopmentalSummaryData(activeProfile, stalenessThreshold);
              
  // 🔍 DEBUG: Log what data we're getting
  console.log('📊 HomeView Skills Development data:', {
    profileId: activeProfile?.id,
    isPremium: activeProfile?.isPremium,
    summaryDataLength: summaryData?.length,
    domains: summaryData?.map(d => d.key),
    devageKeys: Object.keys(activeProfile?.devage || {}),
    devfloorKeys: Object.keys(activeProfile?.devfloor || {}),
    devresultscoreKeys: Object.keys(activeProfile?.devresultscore || {})
  });

              if (!summaryData || summaryData.length === 0) {
                return <Text style={styles.loadingText}>No milestone data yet</Text>;
              }
              // Sort best → worst: primary devresult descending (nulls last),
              // secondary devdelay descending (handles ties at Advanced ≥100 and Needs Help ≤0)
              const sorted = [...summaryData].sort((a, b) => {
                if (a.isCapped && !b.isCapped) return -1;
                if (!a.isCapped && b.isCapped) return 1;
                const ra = a.devresult ?? -Infinity;
                const rb = b.devresult ?? -Infinity;
                if (rb !== ra) return rb - ra;
                const da = a.devdelay ?? -Infinity;
                const db = b.devdelay ?? -Infinity;
                return db - da;
              });
              return (
                <View>
                  {sorted.map(item => {
                    const isStale = item.staleness?.isStale === true;
                    const notAssessed = !item.isCapped && (item.devresult === null || item.devresult === undefined);
                    const { label, color } = getSkillResultLabel(item.devresult, item.isCapped);
                    return (
                      <TouchableOpacity
                        key={item.key}
                        style={styles.skillResultRow}
                        onPress={() => {
                          const isPremium = appConfig.isPremium();
                          //const isPremium = activeProfile?.isPremium ?? appConfig.isPremium();
                          const mapped = mapDomainForMilestones(item.key, isPremium);
                          if (onNavigateWithDomain) onNavigateWithDomain('Milestones', mapped);
                          else onTabChange('Milestones');
                        }}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.skillResultDomain} numberOfLines={1}>{item.domain}</Text>
                        <View style={styles.skillResultBadgeCell}>
                          {isStale ? (
                            <Text style={styles.skillResultPlainText}>Update needed</Text>
                          ) : notAssessed ? (
                            <Text style={styles.skillResultPlainText}>Not assessed</Text>
                          ) : (
                            <View style={[styles.skillResultBadge, { backgroundColor: color + '22', borderColor: color }]}>
                              <Text style={[styles.skillResultLabel, { color }]}>{label}</Text>
                            </View>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              );
            })()}

            {/* Active Concerns */}
            <View style={styles.homeSubSectionDivider} />
            <Text style={styles.homeSubSectionTitle}>Active Concerns</Text>
            {(() => {
              const activeConcerns = (activeProfile.concernsstate ?? []).filter(
                (c: any) => c.action != null && c.action > 0 && !(c.mvarid as string).startsWith('domain_viewed_')
              );
              return (
                <TouchableOpacity
                  style={styles.activeConcernsRow}
                  onPress={() => onTabChange('Concerns')}
                  activeOpacity={0.7}
                >
                  <Text style={styles.activeConcernsCount}>{activeConcerns.length}</Text>
                  <Text style={styles.activeConcernsLabel}>
                    {activeConcerns.length === 1 ? 'concern flagged for action' : 'concerns flagged for actions'}
                  </Text>
                </TouchableOpacity>
              );
            })()}
          </View>

          {/* Today’s Actions */}
          <View style={[styles.developmentSection, { flex: isWide ? 1 : undefined, marginBottom: 0 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>Today’s Actions  🌱</Text>
              <TouchableOpacity onPress={() => onTabChange('Actions')}>
                <Text style={styles.navigationLinkText}>Update →</Text>
              </TouchableOpacity>
            </View>
            <ActionPlanSummary
              actionsState={activeProfile?.actionsstate ?? []}
              actionDef={actionDef}
              prescriptionEvents={activeProfile?.prescriptiontrack?.events ?? []}
              referralEvents={activeProfile?.referraltrack?.events ?? []}
              mode="todayOnly"
              showTodayHeader={false}
              todayEmptyText="No actions due today"
              showRankBtns={false}
              forceSingleRowOnMobile={true}
              onGroupPress={(group, item) => {
                const isWellCheckTracked = (item?.mvar || '').toLowerCase().startsWith('wellcheck_');
                if (isWellCheckTracked) {
                  onTabChange('Well-Visit');
                  return;
                }
                if (onNavigateWithDomain) onNavigateWithDomain('Actions', group);
                else onTabChange('Actions');
              }}
            />
          </View>

        </View>

        {/* Row 1: Developmental Age + Concerns */}
        <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, marginBottom: 20, alignItems: 'stretch' }}>

          {/* Developmental Age */}
          <View style={[styles.developmentSection, { flex: isWide ? 1 : undefined, marginBottom: 0 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>Developmental Age  📊</Text>
              <TouchableOpacity onPress={() => onTabChange('Milestones')}>
                <Text style={styles.navigationLinkText}>Update →</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionSubtitle}>
              Developmental age is the most probable age of an average child with the same milestone skills.
            </Text>
            {activeProfile && activeProfile.devage && activeProfile.devdelay && activeProfile.devresultscore ? (
              <>
                <DevelopmentalSummaryTable 
                  profile={activeProfile} 
                  onTabChange={onTabChange} 
                  onNavigateWithDomain={onNavigateWithDomain} 
                  stalenessThreshold={stalenessThreshold}
                  showLastUpdate={showLastUpdate}
                />
                <View style={styles.colorKey}>
                  <View style={styles.keyTitleRow}>
                    <Text style={styles.colorKeyTitle}>Key:</Text>
                    <TouchableOpacity onPress={() => setShowLearnMore(true)}>
                      <Text style={styles.learnMoreText}>Learn more</Text>
                    </TouchableOpacity>
                  </View>
                  <View style={styles.colorKeyItems}>
                    <View style={styles.colorKeyItem}>
                      <View style={[styles.colorKeyDot, { backgroundColor: '#ff7a7aff' }]} />
                      <Text style={styles.colorKeyText}>Below Cutoff</Text>
                    </View>
                    <View style={styles.colorKeyItem}>
                      <View style={[styles.colorKeyDot, { backgroundColor: '#edff48ff' }]} />
                      <Text style={styles.colorKeyText}>Close to Cutoff</Text>
                    </View>
                    <View style={styles.colorKeyItem}>
                      <View style={[styles.colorKeyDot, { backgroundColor: '#b7e0c1ff' }]} />
                      <Text style={styles.colorKeyText}>Above Cutoff</Text>
                    </View>
                    <View style={styles.colorKeyItem}>
                      <View style={[styles.colorKeyDot, { backgroundColor: '#5cb85c' }]} />
                      <Text style={styles.colorKeyText}>Above the Mean</Text>
                    </View>
                  </View>
                </View>
              </>
            ) : (
              <View style={styles.loadingState}>
                <Text style={styles.loadingText}>Developmental data will appear after first milestone assessment</Text>
              </View>
            )}
          </View>

          {/* Concerns */}
          <View style={[styles.developmentSection, { flex: isWide ? 1 : undefined, marginBottom: 0 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>Flagged Concerns  🔍</Text>
              <TouchableOpacity onPress={() => onTabChange('Concerns')}>
                <Text style={styles.navigationLinkText}>Update →</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionSubtitle}>List of concerns that you have selected to take actions to Track, proactively Manage or Seek Help.</Text>
            {activeProfile ? (() => {
              const textMap = new Map<string, string>();
              const domainKeyMap = new Map<string, string>();
              if (concernData) {
                for (const group of concernData.groups) {
                  for (const item of group.items) {
                    const key = `${group.mvarName.toLowerCase()}_${item.keyValue}`;
                    textMap.set(key, item.text);
                    domainKeyMap.set(key, group.domainKey);
                  }
                }
              }
              const ACTION_META: Record<number, { label: string; color: string }> = {
                1: { label: 'Track',     color: '#3498db' },
                2: { label: 'Manage',    color: '#10b981' },
                3: { label: 'Seek Help', color: '#e74c3c' },
              };
              const actionMVarTextMap = new Map<string, string>();
              if (actionDef) {
                for (const group of actionDef.actionGroups) {
                  for (const item of group.actionMVars) {
                    actionMVarTextMap.set(item.mvar.toLowerCase(), item.text);
                  }
                }
              }
              const domainIconMap = new Map<string, string>(getConcernDomains().map(d => [d.key as string, d.icon]));
              const DEVAGE_NAMES: Record<string, string> = {
                communication: 'Communication', motor: 'Motor Skills', finemotor: 'Fine Motor',
                grossmotor: 'Gross Motor', cognitive: 'Cognitive', socialemotional: 'Social-Emotional',
              };
              const DEVAGE_ICONS: Record<string, string> = {
                communication: '💬', motor: '🏃', finemotor: '✋',
                grossmotor: '🤸', cognitive: '🧠', socialemotional: '😊',
              };
              const withAction = (activeProfile.concernsstate ?? []).filter((c: any) => c.action != null && c.action > 0);
              if (withAction.length === 0) {
                return (
                  <View style={styles.loadingState}>
                    <Text style={styles.loadingText}>{'No flagged concerns yet\nSelect concerns and assign actions in the Concerns tab'}</Text>
                  </View>
                );
              }
              const allStates: any[] = activeProfile.concernsstate ?? [];
              const sentinelStates = allStates.filter((c: any) => (c.mvarid as string).startsWith('domain_viewed_'));
              const timestamps = sentinelStates.map((c: any) => c.lastUpdated).filter(Boolean) as string[];
              const oldestTimestamp = timestamps.length > 0 ? timestamps.reduce((a, b) => (a < b ? a : b)) : null;
              const newestTimestamp = timestamps.length > 0 ? timestamps.reduce((a, b) => (a > b ? a : b)) : null;
              const concernStaleness = checkDomainStaleness(newestTimestamp, concernFreshnessThreshold);
              const concernsNeedUpdating = concernStaleness.isStale || newestTimestamp === null;
              return (
                <View>
                  {[...withAction].sort((a: any, b: any) => (b.action ?? 0) - (a.action ?? 0)).map((c: any) => {
                    const mvaridLower = (c.mvarid as string).toLowerCase();
                    const CN_MEASURE_CODE_TO_DOMAIN: Record<string, string> = {
                      lc: 'communication', mt: 'motor', fm: 'finemotor',
                      gm: 'grossmotor', cg: 'cognitive', se: 'socialemotional',
                    };
                    const isMeasuresCn     = mvaridLower.startsWith('cn_') && mvaridLower.endsWith('_measure');
                    const isMeasuresLegacy1 = mvaridLower.endsWith('_concern') && !mvaridLower.startsWith('domain_viewed_');
                    const isMeasuresLegacy = mvaridLower.startsWith('devage_');
                    const isDevage = isMeasuresCn || isMeasuresLegacy1 || isMeasuresLegacy;
                    const devageDomainKey = isMeasuresCn
                      ? (CN_MEASURE_CODE_TO_DOMAIN[mvaridLower.slice(3, -'_measure'.length)] ?? '')
                      : isMeasuresLegacy1 ? mvaridLower.slice(0, -'_concern'.length)
                      : isMeasuresLegacy ? mvaridLower.slice('devage_'.length) : '';
                    const actionDefText = actionMVarTextMap.get(mvaridLower) ?? '';
                    const text = isDevage
                      ? (actionDefText || DEVAGE_NAMES[devageDomainKey] || devageDomainKey)
                      : (textMap.get(mvaridLower) ?? c.mvarid);
                    const domainKey = isDevage ? 'measures' : (domainKeyMap.get(mvaridLower) ?? '');
                    const icon = isDevage
                      ? (DEVAGE_ICONS[devageDomainKey] ?? '📊')
                      : (domainIconMap.get(domainKey) ?? '');
                    const meta = ACTION_META[c.action as number] ?? { label: `Action ${c.action}`, color: '#888' };
                    return (
                      <TouchableOpacity
                        key={c.mvarid}
                        style={styles.concernSummaryRow}
                        onPress={() => onNavigateWithDomain?.('Concerns', domainKey || undefined)}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.concernDomainIcon}>{icon}</Text>
                        <Text style={[styles.concernSummaryText, { color: '#2563eb' }]} numberOfLines={2}>{text}</Text>
                        <View style={[styles.concernActionBadge, { backgroundColor: meta.color }]}>
                          <Text style={styles.concernActionBadgeText}>{meta.label}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity style={styles.navigationLink} onPress={() => onTabChange('Concerns')}>
                    <Text style={styles.navigationLinkText}>Select a concern to update</Text>
                  </TouchableOpacity>
                  {concernsNeedUpdating && (
                    <View style={styles.stalenessWarning}>
                      <Text style={styles.stalenessWarningText}>⚠️ Concerns need updating</Text>
                    </View>
                  )}
                  {showLastUpdate && !concernsNeedUpdating && oldestTimestamp && (
                    <View style={styles.stalenessWarning}>
                      <Text style={styles.lastUpdateText}>Oldest update: {formatComputationTimestamp(oldestTimestamp)}</Text>
                    </View>
                  )}
                </View>
              );
            })() : (
              <View style={styles.loadingState}>
                <Text style={styles.loadingText}>Concern data will appear after first assessment</Text>
              </View>
            )}
          </View>

        </View>

        {/* Row 2: Action Plan + Quick Links */}
        <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, marginBottom: 20, alignItems: 'stretch' }}>

          {/* Action Plan */}
          <View style={[styles.developmentSection, { flex: isWide ? 1 : undefined, marginBottom: 0 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>Action Plan  ✅</Text>
              <TouchableOpacity onPress={() => onTabChange('Actions')}>
                <Text style={styles.navigationLinkText}>Update →</Text>
              </TouchableOpacity>
            </View>
            <ActionPlanSummary
              actionsState={activeProfile?.actionsstate ?? []}
              actionDef={actionDef}
              prescriptionEvents={activeProfile?.prescriptiontrack?.events ?? []}
              referralEvents={activeProfile?.referraltrack?.events ?? []}
              showRankBtns={false}
              forceSingleRowOnMobile={true}
              onGroupPress={(group, item) => {
                const isWellCheckTracked = (item?.mvar || '').toLowerCase().startsWith('wellcheck_');
                if (isWellCheckTracked) {
                  onTabChange('Well-Visit');
                  return;
                }
                if (onNavigateWithDomain) onNavigateWithDomain('Actions', group);
                else onTabChange('Actions');
              }}
            />
          </View>

          {/* Quick Links */}
          <View style={[styles.quickActions, { flex: isWide ? 1 : undefined }]}>
            <Text style={styles.sectionTitle}>Quick Links</Text>
            <View style={styles.actionsGrid}>
              <TouchableOpacity style={styles.actionCard} onPress={() => onTabChange('ResourceList')}>
                <Text style={styles.actionEmoji}>📋</Text>
                <Text style={styles.actionTitle}>Resource List</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionCard} onPress={() => onTabChange('Actions')}>
                <Text style={styles.actionEmoji}>✅</Text>
                <Text style={styles.actionTitle}>Manage Actions</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionCard} onPress={() => onTabChange('Guidance')}>
                <Text style={styles.actionEmoji}>📚</Text>
                <Text style={styles.actionTitle}>Guidance</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionCard} onPress={() => onTabChange('Account')}>
                <Text style={styles.actionEmoji}>👫</Text>
                <Text style={styles.actionTitle}>Refer a Friend</Text>
              </TouchableOpacity>
            </View>
          </View>

        </View>

        </View>{/* end maxWidth wrapper */}

      <ProfileDropdown
        isVisible={dropdownVisible}
        onClose={() => setDropdownVisible(false)}
        onSelectProfile={setActiveProfile}
        onAddProfile={handleAddProfile}
        profiles={selectableProfiles}
        activeProfileId={activeProfileId}
        canAddProfile={canAddProfile}
        maxProfiles={maxProfiles}
      />

      {/* Learn More Modal */}
      <Modal
        visible={showLearnMore}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLearnMore(false)}
      >
        <View style={styles.learnMoreModalOverlay}>
          <View style={styles.learnMoreModal}>
            <Text style={styles.learnMoreTitle}>Understanding Developmental Milestones</Text>
            <ScrollView style={styles.learnMoreContent}>
              <Text style={styles.learnMoreText}>Developmental milestones were developed by the Centers for Disease Control (CDC) for their Learn the Signs. Act Early. (LTSAE) program in collaboration with the American Academy of Pediatrics (AAP).</Text>
              
              <Text style={styles.learnMoreSubtitle}>Development Age:</Text>
              <Text style={styles.learnMoreText}>The most probable age of an average child with the same milestone skills as your child.</Text>
              
              <Text style={styles.learnMoreSubtitle}>Delay:</Text>
              <Text style={styles.learnMoreText}>The difference between your child's developmental age and their chronological age in each domain.</Text>
              
              <Text style={styles.learnMoreSubtitle}>Result Categories:</Text>
              <Text style={styles.learnMoreText}><Text style={{color: '#ff7a7a', fontSize: 16}}>●</Text> <Text style={{color: '#000000'}}>Below Cutoff</Text>: Indicates delays potentially requiring professional attention</Text>
              <Text style={styles.learnMoreText}><Text style={{color: '#edff48', fontSize: 16}}>●</Text> <Text style={{color: '#000000'}}>Close to Cutoff</Text>: Indicates delays where you should focus attention</Text>
              <Text style={styles.learnMoreText}><Text style={{color: '#b7e0c1', fontSize: 16}}>●</Text> <Text style={{color: '#000000'}}>Above Cutoff</Text>: Within monitoring range, continue observation</Text>
              <Text style={styles.learnMoreText}><Text style={{color: '#5cb85c', fontSize: 16}}>●</Text> <Text style={{color: '#000000'}}>Above the Mean</Text>: Indicates advanced development</Text>
              
              <Text style={[styles.learnMoreText, {marginTop: 7}]}>These results use the same 1 and 2 standard deviation cutoffs used by the Ages and Stages Questionnaires® (ASQ). Premium milestone results are a direct, more accurate, substitution for ASQ results.</Text>
            </ScrollView>
            <TouchableOpacity 
              style={styles.closeModalButton}
              onPress={() => setShowLearnMore(false)}
            >
              <Text style={styles.closeModalButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    paddingTop: 26,
    paddingBottom: 16,
    backgroundColor: '#3b82f6',
  },
  fullScreenContent: {
    flexGrow: 1,
    backgroundColor: '#3b82f6',
  },
  
  // Profile Header Styles
  profileHeader: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  profileHeaderTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
  },
  profileAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarImage: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  avatarText: {
    fontSize: 52,
    lineHeight: 58,
  },
  profileDetails: {
    flexShrink: 1,
    minWidth: 0,
  },
  profileName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  profileAge: {
    fontSize: 13,
    color: '#e2e8f0',
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  premiumBadge: {
    fontSize: 12,
    color: '#f39c12',
    fontWeight: '600',
  },
  dirtyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  dirtyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f59e0b',
    marginRight: 5,
  },
  dirtyBadgeText: {
    fontSize: 11,
    color: '#b45309',
    fontWeight: '500',
  },
  dropdownNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dropdownDirtyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f59e0b',
    marginLeft: 6,
    marginTop: 1,
  },
  dropdownAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  dropdownAvatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  dropdownAvatarEmoji: {
    fontSize: 36,
    lineHeight: 42,
  },
  dropdownProfileName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 2,
  },
  dropdownProfileAge: {
    fontSize: 13,
    color: '#7f8c8d',
  },
  syncIconButton: {
    width: Platform.OS === 'web' ? 42 : 30,
    height: Platform.OS === 'web' ? 42 : 30,
    borderRadius: Platform.OS === 'web' ? 8 : 6,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: Platform.OS === 'web' ? 8 : 6,
    marginRight: 2,
  },
  syncIconButtonDisabled: {
    opacity: 0.5,
  },
  syncIconText: {
    fontSize: Platform.OS === 'web' ? 22 : 16,
  },
  profileAvatarDetailsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  dropdownArrow: {
    fontSize: Platform.OS === 'web' ? 16 : 22,
    color: '#95a5a6',
    marginHorizontal: 8,
    flexShrink: 0,
  },
  brandLogo: {
    width: 160,
    height: 72,
    borderRadius: 4,
  },
  
  // Call to Action Styles
  callToActionSection: {
    backgroundColor: '#fff3cd',
    borderColor: '#ffc107',
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
  },
  callToActionText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#856404',
    textAlign: 'center',
  },
  
  // Modal and Dropdown Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    margin: 20,
    minWidth: 280,
    maxHeight: 400,
  },
  dropdownTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    textAlign: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
  },
  profileOption: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
  },
  activeProfileOption: {
    backgroundColor: '#e8f4fd',
  },
  profileOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileInfo: {
    flex: 1,
    marginLeft: 12,
  },
  addProfileButton: {
    padding: 16,
    alignItems: 'center',
  },
  addProfileButtonDisabled: {
    opacity: 0.55,
  },
  addProfileText: {
    fontSize: 16,
    color: '#3498db',
    fontWeight: '600',
  },
  addProfileTextDisabled: {
    color: '#7f8c8d',
  },
  
  // Development Section Styles
  developmentSection: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 16,
  },
  domainsGrid: {
    gap: 12,
  },
  domainCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 4,
    marginBottom: 8,
  },
  domainHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  domainName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
  },
  percentile: {
    fontSize: 12,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  domainAge: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  domainSubtext: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  loadingState: {
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  premiumNote: {
    fontSize: 12,
    color: '#f39c12',
    fontStyle: 'italic',
    marginTop: 12,
    textAlign: 'center',
  },
  
  // Developmental Summary Table Styles
  summaryTable: {
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e1e8ed',
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8f9fa',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2c3e50',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f3f5',
    alignItems: 'center',
  },
  tableRowEven: {
    backgroundColor: '#f8f9fa',
  },
  tableText: {
    fontSize: 13,
    color: '#2c3e50',
    textAlign: 'center',
  },
  domainColumn: {
    flex: 2.5,
    textAlign: 'left',
  },
  ageColumn: {
    flex: 1.5,
  },
  delayColumn: {
    flex: 1.2,
  },
  scoreColumn: {
    flex: 1.2,
  },
  
  // Staleness warning styles
  stalenessWarning: {
    alignItems: 'center',
    paddingVertical: 3,
  },
  stalenessWarningText: {
    fontSize: 13,
    color: '#e74c3c',
    fontWeight: '500',
    textAlign: 'center',
  },
  
  // Color Key Styles
  colorKey: {
    marginTop: 12,
    marginLeft: 16,
  },
  colorKeyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  colorKeyItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 8,
  },
  colorKeyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    flex: 1,
    minWidth: '48%',
  },
  colorKeyDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginRight: 6,
  },
  colorKeyText: {
    fontSize: 11,
    color: '#34495e',
    flex: 1,
  },
  
  // Summary Card Styles
  summaryStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 8,
  },
  summaryStat: {
    alignItems: 'center',
    flex: 1,
    paddingVertical: 8,
  },
  summaryStatDivider: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#e1e8ed',
  },
  summaryStatValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  summaryStatLabel: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 2,
  },
  summaryActionButton: {
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  summaryActionButtonText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 15,
  },
  concernSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 6,
  },
  concernDomainIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
  concernSummaryText: {
    flex: 1,
    fontSize: 14,
    color: '#2c3e50',
    lineHeight: 20,
  },
  concernActionBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    flexShrink: 0,
  },
  concernActionBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#ffffff',
  },

  // Quick Actions Styles
  quickActions: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    width: '48%',
    minHeight: 80,
  },
  actionEmoji: {
    fontSize: 24,
    marginBottom: 8,
  },
  actionTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#2c3e50',
    textAlign: 'center',
  },
  
  // Empty State Styles
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 16,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 16,
    color: '#e2e8f0',
    textAlign: 'center',
    marginBottom: 30,
    lineHeight: 24,
  },
  createProfileButton: {
    backgroundColor: '#10b981',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  createProfileButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  navigationLink: {
    paddingTop: 10,
    paddingBottom: 2,
    alignItems: 'center',
  },
  navigationLinkText: {
    fontSize: 12,
    color: '#3498db',
    marginBottom: 2,
  },
  keyTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  learnMoreText: {
    fontSize: 12,
    color: '#3498db',
    marginBottom: 2,
  },
  learnMoreModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  learnMoreModal: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    padding: 20,
    maxHeight: '80%',
    width: '90%',
  },
  learnMoreTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 15,
    textAlign: 'center',
  },
  learnMoreContent: {
    maxHeight: 300,
  },
  learnMoreSubtitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginTop: 10,
    marginBottom: 5,
  },
  closeModalButton: {
    backgroundColor: '#3498db',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    alignSelf: 'center',
    marginTop: 15,
  },
  closeModalButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  lastUpdateText: {
    fontSize: 11,
    color: '#7f8c8d',
    fontWeight: '400',
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // ── How are we doing card ────────────────────────────────────────────────────────────
  homeSubSectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#64748b',
    marginBottom: 6,
    marginTop: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.6,
  },
  homeSubSectionDivider: {
    height: 1,
    backgroundColor: '#e2e8f0',
    marginVertical: 12,
  },
  skillResultRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  skillResultDomain: {
    flex: 3,
    fontSize: 13,
    color: '#334155',
    marginLeft: 12,
  },
  skillResultBadgeCell: {
    flex: 2,
    alignItems: 'center' as const,
  },
  skillResultPlainText: {
    fontSize: 11,
    color: '#94a3b8',
    fontStyle: 'italic' as const,
  },
  skillResultBadge: {
    borderRadius: 5,
    borderWidth: 1,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  skillResultLabel: {
    fontSize: 11,
    fontWeight: '700' as const,
  },
  activeConcernsRow: {
    flexDirection: 'row' as const,
    alignItems: 'baseline' as const,
    gap: 6,
    marginTop: 4,
    marginLeft: 12,
  },
  activeConcernsCount: {
    fontSize: 32,
    fontWeight: '700' as const,
    color: '#10b981',
  },
  activeConcernsLabel: {
    fontSize: 13,
    color: '#64748b',
    flexShrink: 1,
  },

});
